import React, { useState } from 'react'
import { useDispatch } from 'dva'
import { Breadcrumb, Spin, Typography, message } from 'antd'
import { useLocation } from 'react-router-dom'
import { InputNumber, CategoryTitle } from 'components'

import { useRequest } from '@sisyphe/hooks'
import * as api from 'services/store'
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb'
import {
  Title,
  DetailImg,
  InfoItem,
  Button,
  ProductPrice,
  Card,
  DetailBreadcrumb,
} from '../components'
import qs from 'query-string'
import { getAuthorName } from 'utils'
import styles from './index.module.less'

type Props = {}
type SearchProps = { spuCode?: string }

const Detail = (props: Props) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [breadcrumbItems, setBreadcrumbItems] = useState<ItemType[]>([])
  const [amount, setAmount] = useState(1)

  /**商城商品详情查询-App */
  const { data, loading } = useRequest(async () => {
    const search = location.search
    const { spuCode }: SearchProps = qs.parse(search)
    const { data: detailData } = await api.queryDetailQueryUsingPOST({
      spuCode: spuCode!,
    })

    const items = detailData?.frontCategoryTree?.map(c => ({
      title: (
        <CategoryTitle code={c.categoryCode}>{c?.categoryName}</CategoryTitle>
      ),
    }))

    // 查询库存
    const { data: stocks } = await api.judgeCacheUsingPOST({
      skuCodeList: detailData?.skus?.map(c => c.skuCode),
    })

    // 库存信息
    const skuStocks = {}
    stocks?.forEach(item => (skuStocks[item.skuCode] = item))

    setBreadcrumbItems([
      ...items,
      { title: <DetailBreadcrumb>{detailData.appShowName}</DetailBreadcrumb> },
    ])
    return { detailData, skuStock: stocks?.[0] }
  })

  /**购物车管理-添加购物车 */
  const { run: shopCartAddUsingPOST, loading: addLoading } = useRequest(
    async () => {
      const skuInfo = detailData?.skus?.[0]

      const { data: stocks } = await api.judgeCacheUsingPOST({
        skuCodeList: [skuInfo?.skuCode],
      })

      if (stocks?.[0]?.usableStock <= 0) {
        setAmount(0)
        message.warning('加购失败，商品已缺货')
        return false
      }

      // 提交时还需要校验库存
      if (amount > stocks?.[0]?.usableStock) {
        setAmount(stocks?.[0]?.usableStock)
        message.warning('加购数量超库存')
        return false
      }

      const params = {
        num: amount,
        price: skuInfo?.standardPrice,
        productType: detailData?.contentTtype,
        salePrice: skuInfo?.salePrice,
        skuCode: skuInfo?.skuCode,
        spuCode: detailData?.spuCode,
      }
      await api.shopCartAddUsingPOST(params as any)
      dispatch({ type: 'cart/getShopCartNum' })
      message.success('已加入购物车')
    },
    { manual: true }
  )

  const { detailData, skuStock } = data || {}

  /**
   * 获取不同类型的作者
   * 每种类型的作者确实有独立的 List，但是List里面可能有其他类型的作者！
   */
  const getAuthorInfo = (
    list: string,
    type: string
  ): SwaggerDTO.AuthorInfoDTO[] =>
    detailData?.[list]?.filter(item => item.authorType === type) ?? []

  /**作者信息 */
  const authorInfo = getAuthorInfo('author', '1')

  /**详情信息 */
  const detailInfo = detailData?.detailInfo
  const details = [
    {
      label: '书名：',
      value: detailData?.spuName,
    },
    {
      label: '外文书名：',
      value: detailInfo?.foreignName,
    },
    {
      label: 'ISBN：',
      value: detailData?.pressInfo.isbn,
    },
    {
      label: '出版社名称：',
      value: detailData?.pressInfo?.pressName,
    },
    {
      label: '图书策划出版社：',
      value: detailData?.pressInfo?.publishCompany,
    },
    {
      label: '出版日期：',
      value: detailData?.pressInfo?.publishDate,
    },
    {
      label: '作者：',
      value: getAuthorName(authorInfo),
    },
    {
      label: '译者：',
      value: getAuthorName(getAuthorInfo('translator', '2')),
    },

    {
      label: '编者：',
      value: getAuthorName(getAuthorInfo('editors', '3')),
    },
    {
      label: '绘者：',
      value: getAuthorName(getAuthorInfo('drawers', '4')),
    },
    {
      label: '校注：',
      value: getAuthorName(getAuthorInfo('reviewer', '7')),
    },
    {
      label: '注释：',
      value: getAuthorName(getAuthorInfo('commentor', '8')),
    },

    {
      label: '正文语种：',
      value: detailInfo?.lingType,
    },
    {
      label: '用纸：',
      value: detailInfo?.paperType,
    },
    {
      label: '开本：',
      value: detailInfo?.kaiben,
    },

    {
      label: '装订形式：',
      value: detailInfo?.bindFormat,
    },
    {
      label: '装帧：',
      value: detailInfo?.binding,
    },
    {
      label: '字数（千字）',
      value: detailInfo?.words,
    },
    {
      label: '页数：',
      value: detailInfo?.pages,
    },
  ]

  return (
    <Spin spinning={loading}>
      <div className={styles.box}>
        <Breadcrumb items={breadcrumbItems} />
        <div className={styles.detailInfo}>
          <DetailImg
            imgList={[
              detailData?.appImgUrl,
              ...(detailData?.bannerImage?.map(c => c?.mediaUrl) || []),
            ]}
          />
          <div className={styles.info}>
            <div className={styles.detail}>
              <Title>{detailData?.appShowName}</Title>
              <InfoItem label="定 价：">
                <ProductPrice
                  minPrice={detailData?.minStandardPrice}
                  maxPrice={detailData?.maxStandardPrice}
                />
              </InfoItem>
              <InfoItem label="ISBN：">{detailData?.pressInfo.isbn}</InfoItem>
              <InfoItem label="作 者：">{getAuthorName(authorInfo)}</InfoItem>
              <InfoItem label="出版社：">
                {detailData?.pressInfo?.pressName}
              </InfoItem>
            </div>
            {!loading && (
              <AddCardButton
                detailData={detailData}
                skuStock={skuStock}
                amount={amount}
                setAmount={setAmount}
                addLoading={addLoading}
                shopCartAddUsingPOST={shopCartAddUsingPOST}
              />
            )}
          </div>
        </div>
        <div className={styles.productDetail}>
          <p className={styles.productDetailTitle}>商品详情</p>
          <div className={styles.desc}>
            {details.map(
              (item, key) =>
                item.value && (
                  <span key={key}>
                    {item.label}
                    {item.value}
                  </span>
                )
            )}
          </div>
          {!!authorInfo?.length &&
            authorInfo?.some(item => !!item.authorBrief) && (
              <Card title="作者简介">
                {authorInfo?.map((item, index) =>
                  item?.authorBrief ? (
                    <>
                      <div key={index}>{item?.authorBrief}</div>
                      <br />
                    </>
                  ) : null
                )}
              </Card>
            )}
          {detailData?.contentDetail?.contentBrief && (
            <Card title="内容简介">
              {detailData?.contentDetail.contentBrief}
            </Card>
          )}
          {detailData?.saleInfoDetail?.honors && (
            <Card title="获奖信息">{detailData?.saleInfoDetail.honors}</Card>
          )}
          {detailData?.saleInfoDetail?.editorRecommend && (
            <Card title="编辑推荐">
              {detailData?.saleInfoDetail.editorRecommend}
            </Card>
          )}
          <div className={styles.langImg}>
            {detailData?.skus?.[0].longPic?.map((item, index) => (
              <img key={index} src={item} alt="" />
            ))}
          </div>
        </div>
      </div>
    </Spin>
  )
}

export default Detail

const AddCardButton = ({
  detailData,
  skuStock,
  amount,
  setAmount,
  addLoading,
  shopCartAddUsingPOST,
}) => {
  if (detailData?.localCategoryDownFlag) {
    return <Typography.Title level={5}>抱歉，商品已下架</Typography.Title>
  }

  if (
    detailData?.spuStatus === 1 ||
    detailData?.spuAdvanceSale === 2 ||
    !detailData?.onSaleStatus ||
    detailData?.ifExpired
  ) {
    return <Typography.Title level={5}>抱歉，商品已下架</Typography.Title>
  }

  if (!detailData?.ifExpired && detailData?.spuStatus === 2) {
    return <Typography.Title level={5}>商品缺货</Typography.Title>
  }

  if (!detailData?.ifExpired && !detailData?.spuStatus) {
    return (
      <div>
        <div className={styles.number}>
          <InfoItem label="数 量：">
            <InputNumber
              disabled={!skuStock?.usableStock}
              max={skuStock?.usableStock}
              value={amount}
              onChange={e => setAmount(e)}
            />
          </InfoItem>
        </div>
        <Button
          type="primary"
          loading={addLoading}
          onClick={shopCartAddUsingPOST}
        >
          加入购物车
        </Button>
      </div>
    )
  }

  return <></>
}
